import type { ThemeDefinition, VuetifyOptions } from 'vuetify';

/**
 * Overrides Vuetify's built-in `light` theme, so any token not set here keeps
 * its default through Vuetify's deep merge. `success` and `error` mirror
 * `tailwind.config.js`. `primary` deliberately does not: Tailwind's
 * `green.regular` is still #29bf12, so a `bg-green-regular` button and a
 * `primary` one are different greens until Tailwind follows.
 */
export const leanlawLightTheme: ThemeDefinition = {
  dark: false,
  colors: {
    // Style kit "Brand deep", the kit's AA-safe green. Used for fills too, where
    // the kit uses #29bf12: a white label on it and a label drawn in it
    // (outlined, text) both pass AA, and so does a checked box's shape.
    primary: '#1B7C0C',
    // The kit has nothing darker; this is the same step down that tailwind
    // green.dark is from green.regular. Unset, Vuetify's stock blue would apply.
    'primary-darken-1': '#186F0B',
    // tailwind green.regular. Status rather than action, so it stays brighter.
    success: '#29bf12',
    // tailwind negative.regular
    error: '#dc372f',

    // Control boundaries — button borders, field outlines. 3.23:1 on white:
    // WCAG 1.4.11 asks 3:1 for non-text, not the 4.5:1 text figure people reach
    // for first. Derived from CONTROL_BORDER_BASE at `control-border-opacity`
    // so panels and the controls inside them draw one line; a spec asserts it.
    outline: '#8F8F8F',

    // Structural boundaries — panel edges, dividers. Weaker on purpose: a
    // control's edge has to be findable, a grouping line only followable. At
    // equal weight the page reads as a wireframe.
    'outline-variant': '#CCCCCC',

    // Secondary-button fill. A token rather than palette grey-lighten-4 so the
    // label comes from `on-control-surface`; the palette class hardcodes
    // `color: #000 !important`.
    'control-surface': '#F5F5F5',
    'on-control-surface': '#333333',

    // Read-only field fill. A step darker than control-surface: the same #F5F5F5
    // over a whole field reads lighter than it does on a small bordered button.
    'readonly-surface': '#EBEBEB',
    'on-readonly-surface': '#333333',

    'on-primary': '#FFFFFF',
    // Knowingly 2.45:1, below AA. The product already pairs white on #29bf12
    // in ~45 Tailwind call sites, and letting Vuetify auto-compute would flip
    // success chips to black while those stayed white. Brand decision.
    'on-success': '#FFFFFF',
  },

  // Emitted as `--v-<key>` on :root and read by src/css/style.css, so a boundary
  // alpha is written once.
  variables: {
    'control-border-opacity': 0.55,
    'control-border-opacity-hover': 0.87,
    'structure-border-opacity': 0.25,
  },
};

/** The ink both boundary tokens composite from, and the value style.css hardcodes. */
export const CONTROL_BORDER_BASE = '#333333';

/**
 * A checkbox that passes no `color` should already be right. VCheckboxBtn is
 * what a multiple VSelect draws in each option, and it takes no colour from
 * VCheckbox's default.
 */
export const leanlawDefaults: VuetifyOptions['defaults'] = {
  VCheckbox: { color: 'primary' },
  VCheckboxBtn: { color: 'primary' },
};
